import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Mail, Phone, MapPin, Clock, CheckCircle, AlertCircle } from 'lucide-react'
import { api } from '@/lib/api'

const contactInfo = [
  {
    icon: Mail,
    title: 'Имэйл',
    value: 'info@arya.mn',
    description: 'Бидэнтэй имэйлээр холбогдоорой',
  },
  {
    icon: Phone,
    title: 'Утас',
    value: '+976 7700-0000',
    description: 'Ажлын өдрүүдэд холбогдох боломжтой',
  },
  {
    icon: MapPin,
    title: 'Хаяг',
    value: 'Улаанбаатар хот',
    description: 'Монгол улс',
  },
  {
    icon: Clock,
    title: 'Ажлын цаг',
    value: 'Даваа - Баасан',
    description: '09:00 - 18:00',
  },
]

interface FieldErrors {
  name?: string
  email?: string
  message?: string
}

function validateForm(name: string, email: string, message: string): FieldErrors {
  const errors: FieldErrors = {}
  if (!name.trim()) errors.name = 'Нэр оруулна уу'
  if (!email.trim()) {
    errors.email = 'Имэйл оруулна уу'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Зөв имэйл хаяг оруулна уу'
  }
  if (!message.trim()) {
    errors.message = 'Мессеж оруулна уу'
  } else if (message.trim().length < 5) {
    errors.message = 'Мессеж хамгийн багадаа 5 тэмдэгт байх ёстой'
  }
  return errors
}

export default function Contact() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text })
    setTimeout(() => setToast(null), 4000)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const fieldErrors = validateForm(name, email, message)
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    setSubmitting(true)
    try {
      await api.feedback.submit({ name: name.trim(), email: email.trim(), message: message.trim() })
      showToast('success', 'Таны санал хүсэлт амжилттай илгээгдлээ!')
      setName('')
      setEmail('')
      setMessage('')
      setErrors({})
    } catch (err: any) {
      showToast('error', err.message || 'Илгээхэд алдаа гарлаа. Дахин оролдоно уу.')
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass = (field: keyof FieldErrors) =>
    `border rounded-xl px-4 py-3 text-sm focus:outline-none transition ${
      errors[field]
        ? 'border-red-400 focus:border-red-500 bg-red-50/30'
        : 'border-gray-200 focus:border-primary-500'
    }`

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast notification */}
      {toast && (
        <div
          className="fixed top-6 right-6 z-50"
          style={{ animation: 'toast-slide-in 0.3s ease-out' }}
        >
          <div
            className={`flex items-center gap-3 px-5 py-4 rounded-2xl shadow-lg border backdrop-blur-sm ${
              toast.type === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="size-5 text-emerald-500 shrink-0" />
            ) : (
              <AlertCircle className="size-5 text-red-500 shrink-0" />
            )}
            <span className="text-sm font-medium">{toast.text}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-2 text-current opacity-50 hover:opacity-100 transition cursor-pointer"
            >
              &times;
            </button>
          </div>
        </div>
      )}
      <style>{`
        @keyframes toast-slide-in {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-[1024px] mx-auto px-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2.5 text-sm font-medium text-gray-800 bg-white border border-gray-200 rounded-full px-4 py-2.5 shadow-md hover:bg-gray-50 transition"
          >
            <ArrowLeft className="size-5" />
            Буцах
          </Link>
        </div>
      </div>

      {/* Title */}
      <div className="text-center pt-8 pb-6">
        <h1 className="text-3xl md:text-5xl font-bold text-gray-800">ХОЛБОО БАРИХ</h1>
        <p className="text-base text-gray-500 mt-3 max-w-[500px] mx-auto">
          Асуулт, санал хүсэлтээ бидэнд чөлөөтэй илгээгээрэй.
        </p>
      </div>

      <div className="max-w-[1024px] mx-auto px-4 pb-20">
        {/* Contact cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {contactInfo.map((item) => (
            <div
              key={item.title}
              className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col gap-4"
            >
              <div className="size-10 rounded-xl bg-primary-50 flex items-center justify-center">
                <item.icon className="size-5 text-primary-500" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase">{item.title}</p>
                <p className="font-semibold text-gray-800 mt-1">{item.value}</p>
                <p className="text-sm text-gray-500 mt-0.5">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Contact form */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 max-w-[640px] mx-auto">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Санал хүсэлт илгээх</h2>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Нэр</label>
              <input
                type="text"
                placeholder="Таны нэр"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }))
                }}
                className={inputClass('name')}
              />
              {errors.name && <p className="text-xs text-red-500 mt-0.5">{errors.name}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Имэйл</label>
              <input
                type="email"
                placeholder="Таны имэйл хаяг"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }))
                }}
                className={inputClass('email')}
              />
              {errors.email && <p className="text-xs text-red-500 mt-0.5">{errors.email}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Мессеж</label>
              <textarea
                rows={5}
                placeholder="Таны санал хүсэлт..."
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value)
                  if (errors.message) setErrors((prev) => ({ ...prev, message: undefined }))
                }}
                className={`${inputClass('message')} resize-none`}
              />
              {errors.message && <p className="text-xs text-red-500 mt-0.5">{errors.message}</p>}
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="bg-primary-500 text-white rounded-full py-3 text-sm font-semibold hover:bg-primary-600 transition mt-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {submitting ? 'Илгээж байна...' : 'Илгээх'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
